import React, { useEffect, useState } from "react";
import "./AdminDashboard.css";
import {
  getAllAppointments,
  updateAppointmentStatus,
} from "../../../backendServices/appointmentService";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField, // Added TextField for date input
} from "@mui/material";
import { t } from "i18next";
import Notify from "../../Notify";

// Helper function to format the date as dd-mm-yyyy
const getFormattedDate = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Helper function to convert book_date to dd-mm-yyyy format for comparison
const formatDateForComparison = (bookDate) => {
  const [day, month, year] = bookDate.split("-");
  return `${day}-${month}-${year}`;
};

const AdminDashboard = ({ token }) => {
  const [filteredStatus, setFilteredStatus] = useState("all");
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    getFormattedDate(new Date())
  ); // Default to today in dd-mm-yyyy format

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const response = await getAllAppointments(token);
        setAppointments(response.data.appointments);
      } catch (error) {
        console.error("Failed to fetch appointments", error);
      }
    };

    if (token) {
      fetchAppointments();
    }
  }, [token]);

  const filteredAppointments =
    filteredStatus === "all"
      ? appointments.filter((appointment) => {
          if (!selectedDate || selectedDate === "all") return true; // If no date filter or "All" is selected
          return (
            formatDateForComparison(appointment.book_date) === selectedDate
          );
        })
      : appointments.filter(
          (appointment) =>
            appointment.status === filteredStatus &&
            (selectedDate === "all" ||
              formatDateForComparison(appointment.book_date) === selectedDate)
        );

  const handleStatusChange = (event) => {
    setNewStatus(event.target.value);
  };

  const handleSaveStatus = async () => {
    if (!selectedAppointment) return;

    try {
      const updatedAppointment = {
        ...selectedAppointment,
        status: newStatus,
      };

      const response = await updateAppointmentStatus(token, updatedAppointment);

      if (response.status === 200) {
        setAppointments((prevAppointments) =>
          prevAppointments.map((appointment) =>
            appointment.book_id === selectedAppointment.book_id
              ? { ...appointment, status: newStatus }
              : appointment
          )
        );

        setSelectedAppointment(null);
        setNewStatus("");

        Notify(1, "Saved Successfully");
      } else {
        console.error("Failed to update appointment status", response);
      }
    } catch (error) {
      console.error("Error updating appointment status:", error);
    }
  };

  const handleOpenModal = (appointment) => {
    setSelectedAppointment(appointment);
    setNewStatus(appointment.status); // Initialize with current status
  };

  const handleCloseModal = () => {
    setSelectedAppointment(null);
    setNewStatus("");
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        width: "100%",
        minHeight: "100vh",
        padding: 2,
        backgroundColor: "#f9f9f9",
      }}
    >
      <div className="appointments-container">
        <div className="filter-buttons">
          {["all", "pending", "confirmed", "completed", "canceled"].map(
            (status) => (
              <Button
                key={status}
                variant={filteredStatus === status ? "contained" : "outlined"}
                color={status === "all" ? "primary" : "secondary"}
                onClick={() => setFilteredStatus(status)}
                sx={{ margin: "0 8px" }}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            )
          )}

          {/* Date Filter */}
          <TextField
            type="date"
            label="Filter by Date"
            value={selectedDate.split("-").reverse().join("-")} // Convert dd-mm-yyyy to yyyy-mm-dd
            onChange={(e) =>
              setSelectedDate(e.target.value.split("-").reverse().join("-"))
            } // Convert yyyy-mm-dd to dd-mm-yyyy
            sx={{ margin: "0 8px" }}
          />

          {/* Button to reset to today */}
          <Button
            variant="outlined"
            onClick={() => setSelectedDate(getFormattedDate(new Date()))}
            sx={{ margin: "0 8px" }}
          >
            Today
          </Button>

          {/* Button to show all data */}
          <Button
            variant="outlined"
            onClick={() => setSelectedDate("all")}
            sx={{ margin: "0 8px" }}
          >
            All Days
          </Button>
        </div>

        <TableContainer
          component={Paper}
          className="appointments-table"
          sx={{ maxHeight: 500, overflow: "auto" }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* <TableCell>Serial No</TableCell> */}
                <TableCell>Book no</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAppointments.map((appointment) => (
                <TableRow key={appointment.book_id}>
                  {/* <TableCell>{appointment.book_id}</TableCell> */}
                  <TableCell>{appointment.daily_number}</TableCell>
                  <TableCell>{appointment.user_name}</TableCell>
                  <TableCell>{appointment.service}</TableCell>
                  <TableCell>{appointment.book_date}</TableCell>
                  <TableCell>{appointment.time}</TableCell>
                  <TableCell className={`status ${appointment.status}`}>
                    {appointment.status.charAt(0).toUpperCase() +
                      appointment.status.slice(1)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => handleOpenModal(appointment)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={!!selectedAppointment} onClose={handleCloseModal}>
          <DialogTitle>Appointment Details</DialogTitle>
          <DialogContent>
            {selectedAppointment && (
              <div>
                <Typography variant="h6">
                  User: {selectedAppointment.user_name}
                </Typography>
                <Typography variant="body1">
                  Phone: {selectedAppointment.phone}
                </Typography>
                <Typography variant="body1">
                  Email: {selectedAppointment.email}
                </Typography>
                <Typography variant="body1">
                  Service: {selectedAppointment.service}
                </Typography>
                <Typography variant="body1">
                  Date: {selectedAppointment.book_date}
                </Typography>
                <Typography variant="body1">
                  Time: {selectedAppointment.time}
                </Typography>

                <FormControl fullWidth sx={{ marginTop: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newStatus}
                    onChange={handleStatusChange}
                    label="Status"
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </Select>
                </FormControl>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSaveStatus} color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Box>
  );
};

export default AdminDashboard;
